/**
 * Contract for the editor UI ⇄ story data boundary.
 *
 * The implementation lives in `src/product/story/**` (3DPresenter-Codex): it owns the
 * roundtrip-safe Markdown adapter, undo/redo and the session/dirty state. This file only
 * declares the shape the UI builds against, plus a local stand-in used until the real
 * hook lands — so the editor can be developed and reviewed without blocking on it.
 *
 * The UI must NOT parse or write Markdown itself (agreed split, 2026-08-03).
 */

export type StepShape = 'sphere' | 'tile' | 'hexagon' | 'box' | 'cylinder' | 'plane' | 'gltf';

export interface StoryStep {
    id: string;
    title: string;
    body: string;
    shape?: StepShape;
    color?: string;
    /** Image, video or 3D model shown on the object. */
    uri?: string;
    scale?: number;
    /** Unknown and advanced markdown properties are kept losslessly here. */
    metadata?: Record<string, string>;
    metadataOrder?: string[];
    metadataExtraLines?: string[];
}

export interface StoryModel {
    steps: StoryStep[];
    selectedId: string | null;
    select(id: string | null): void;
    update(id: string, patch: Partial<StoryStep>): void;
    add(afterId?: string | null): void;
    remove(id: string): void;
    reorder(from: number, to: number): void;
    markdown: string;
    replaceMarkdown(markdown: string): void;
    /** `key: value` entries of the GLOBAL header (theme, look overrides, layout). Read-only view. */
    globals: Readonly<Record<string, string>>;
    /** The raw `---GLOBAL---` header (fences included) or '' — travels with a draft request unchanged. */
    globalHeader: string;
    /** Patch GLOBAL entries; `undefined` removes a key. Unknown keys and order are preserved. */
    setGlobals(patch: Record<string, string | undefined>): void;
    undo(): void;
    redo(): void;
    canUndo: boolean;
    canRedo: boolean;
    dirty: boolean;
    markSaved(): void;
}

/* ------------------------------------------------------------------ *
 * Local stand-in — replaced by `useStoryModel()` from product/story.
 * Kept deliberately dumb: in-memory only, no Markdown involvement.
 * ------------------------------------------------------------------ */

import { useState, useCallback, useMemo, useRef } from 'react';

const SEED: StoryStep[] = [
    { id: 's1', title: 'Deine erste Idee', body: 'Beginne mit einer klaren Aussage. Jede Überschrift wird zu einer Station im Raum.', shape: 'sphere', color: '#fff5ee', scale: 0.9 },
    { id: 's2', title: 'Der Zusammenhang', body: 'Ordne Inhalte räumlich an und führe dein Publikum mit der Kamera hindurch.', shape: 'sphere', color: '#fff5ee', scale: 0.8 },
    { id: 's3', title: 'Der nächste Schritt', body: 'Ersetze diese Texte durch deine Geschichte — Bilder, Videos oder 3D-Modelle.', shape: 'sphere', color: '#fff5ee', scale: 1.0 },
];

export function useLocalStoryModel(initial: StoryStep[] = SEED): StoryModel {
    const [steps, setSteps] = useState<StoryStep[]>(initial);
    const [selectedId, setSelectedId] = useState<string | null>(initial[0]?.id ?? null);
    const past = useRef<StoryStep[][]>([]);
    const future = useRef<StoryStep[][]>([]);
    const [, bump] = useState(0);

    const commit = useCallback((next: StoryStep[]) => {
        past.current.push(steps);
        future.current = [];
        setSteps(next);
    }, [steps]);

    const update = useCallback((id: string, patch: Partial<StoryStep>) => {
        commit(steps.map(s => (s.id === id ? { ...s, ...patch } : s)));
    }, [steps, commit]);

    const add = useCallback((afterId?: string | null) => {
        const step: StoryStep = { id: `s${Date.now()}`, title: 'Neue Station', body: '', shape: 'sphere', color: '#fff5ee', scale: 0.85 };
        const idx = afterId ? steps.findIndex(s => s.id === afterId) : steps.length - 1;
        const next = [...steps];
        next.splice(idx + 1, 0, step);
        commit(next);
        setSelectedId(step.id);
    }, [steps, commit]);

    const remove = useCallback((id: string) => {
        if (steps.length <= 1) return;
        const idx = steps.findIndex(s => s.id === id);
        commit(steps.filter(s => s.id !== id));
        setSelectedId(steps[Math.max(0, idx - 1)]?.id ?? null);
    }, [steps, commit]);

    const reorder = useCallback((from: number, to: number) => {
        if (from === to) return;
        const next = [...steps];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        commit(next);
    }, [steps, commit]);

    const undo = useCallback(() => {
        const prev = past.current.pop();
        if (!prev) return;
        future.current.push(steps);
        setSteps(prev);
        bump(n => n + 1);
    }, [steps]);

    const redo = useCallback(() => {
        const next = future.current.pop();
        if (!next) return;
        past.current.push(steps);
        setSteps(next);
        bump(n => n + 1);
    }, [steps]);

    return useMemo(() => ({
        steps, selectedId, select: setSelectedId, update, add, remove, reorder,
        markdown: '', replaceMarkdown: () => {}, globals: {}, globalHeader: '', setGlobals: () => {}, undo, redo,
        canUndo: past.current.length > 0, canRedo: future.current.length > 0,
        dirty: past.current.length > 0, markSaved: () => {},
    }), [steps, selectedId, update, add, remove, reorder, undo, redo]);
}
